import React from 'react';

const PlayerForm = ({ 
  players, 
  playerId, 
  activeFilters, 
  getFilteredGames,
  selectedSeasons 
}) => {
  if (!players || !playerId) return null;
  
  // Get filtered games for this player - getFilteredGames already handles finals filtering
  const playerGames = getFilteredGames(players, playerId, activeFilters)
    .sort((a, b) => {
      const yearDiff = parseInt(b.year) - parseInt(a.year);
      if (yearDiff !== 0) return yearDiff;
      
      const roundA = parseInt(a.round.match(/\d+/)?.[0] || '0');
      const roundB = parseInt(b.round.match(/\d+/)?.[0] || '0');
      return roundB - roundA;
    });
  
  if (playerGames.length === 0) return (
    <div className="text-center text-gray-500 dark:text-gray-400 py-4">
      No form data available
    </div>
  );
  
  // Define the periods we want to show
  const periods = [
    { key: 'L3', games: playerGames.slice(0, 3) },
    { key: 'L5', games: playerGames.slice(0, 5) },
    { key: 'L10', games: playerGames.slice(0, 10) },
    { key: 'Season', games: playerGames }
  ];
  
  // Calculate stats for each period
  const calculateStats = (games) => {
    if (games.length === 0) return null;
    
    const stats = {
      'points': 0,
      'tries': 0,
      'all_run_metres': 0,
      'tackles_made': 0,
      'missed_tackles': 0,
      'line_breaks': 0,
      'tackle_breaks': 0,
      'offloads': 0,
      'errors': 0,
      'mins_played': 0,
      'post_contact_metres': 0,
      'kick_metres': 0,
      'performance_points': 0
    };
    
    games.forEach(game => {
      Object.keys(stats).forEach(stat => {
        // Handle special cases for stats that might be "-" or other non-numeric values
        if (stat === 'performance_points') {
          // Calculate performance points for this game
          let pp = 0;
          // 4pts per point scored
          pp += (parseFloat(game['points']) || 0) * 4;
          // 10pts per try assist
          pp += (parseFloat(game['try_assists']) || 0) * 10;
          // 1pt per 10-meter run (rounded down to the nearest 10)
          const runMeters = parseFloat(game['all_run_metres']) || 0;
          pp += Math.floor(runMeters / 10);
          // 5pts per line break
          pp += (parseFloat(game['line_breaks']) || 0) * 5;
          // 1pt per tackle
          pp += (parseFloat(game['tackles_made']) || 0);
          
          stats[stat] += pp;
        } else {
          const value = game[stat];
          if (value === '-' || value === '') {
            stats[stat] += 0;
          } else {
            stats[stat] += parseFloat(value) || 0;
          }
        }
      });
    });
    
    // Calculate averages
    Object.keys(stats).forEach(stat => {
      stats[stat] = (stats[stat] / games.length).toFixed(1);
    });
    
    return stats;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-900 dark:text-white">Form</span>
        </div>
      </div>
      <div className="p-3">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Period</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">PP</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Pts</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Tries</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Run M</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Tkl</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">MT</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">LB</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">TB</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Off</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Err</th>
                <th className="py-2 px-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Mins</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period, index) => {
                const stats = calculateStats(period.games);
                if (!stats) return null;
                
                return (
                  <tr 
                    key={period.key} 
                    className={`
                      ${index !== periods.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}
                      hover:bg-gray-50 dark:hover:bg-gray-700/50
                    `}
                  >
                    <td className="py-2 px-3 text-xs font-medium text-gray-900 dark:text-white">{period.key}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['performance_points']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['points']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['tries']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['all_run_metres']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['tackles_made']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['missed_tackles']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['line_breaks']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['tackle_breaks']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['offloads']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['errors']}</td>
                    <td className="py-2 px-3 text-right text-xs text-gray-600 dark:text-gray-300">{stats['mins_played']}</td>
                  
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerForm; 